// src/components/EventsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import { db, storage, functions } from '../firebase';
import {
    collection, query, where, getDocs, orderBy, Timestamp
} from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';

// Import Font Awesome Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt, faSpinner, faCalendarAlt, faClock, faEdit, faHistory, faRepeat } from '@fortawesome/free-solid-svg-icons';

// Import react-bootstrap components
import { Alert, Modal, Button } from 'react-bootstrap';

// Import the Auth Context hook
import { useAuth } from '../context/AuthContext';

import './EventsPage.css';

// --- Helper Functions ---
const formatEventDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
    });
};

const formatEventTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
};

const isUpcoming = (timestamp) => {
    if (!timestamp) return false;
    const eventDate = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate >= today;
};

// Build resized image path (Firebase Resize Images extension convention)
// Extension outputs WebP format: photo_800x800.webp
const getResizedPath = (originalPath, size) => {
    const lastDot = originalPath.lastIndexOf('.');
    if (lastDot === -1) return originalPath;
    return `${originalPath.substring(0, lastDot)}_${size}.webp`;
};

// --- Event Card Photo Banner ---
function EventCardPhoto({ photoPath }) {
    const [url, setUrl] = useState(null);

    useEffect(() => {
        if (!photoPath) return;
        let cancelled = false;
        const thumbPath = getResizedPath(photoPath, '800x800');
        getDownloadURL(ref(storage, thumbPath))
            .then(downloadUrl => { if (!cancelled) setUrl(downloadUrl); })
            .catch(() => {
                getDownloadURL(ref(storage, photoPath))
                    .then(downloadUrl => { if (!cancelled) setUrl(downloadUrl); })
                    .catch(() => {});
            });
        return () => { cancelled = true; };
    }, [photoPath]);

    if (!url) return null;
    return <img src={url} alt="" className="event-card-banner-img" />;
}

// --- Main Component: EventsPage ---
function EventsPage() {
    const { currentUser, isAdmin, userOrgId, loadingAuth } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Local state
    const [allEvents, setAllEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('upcoming');
    const [generatingBanner, setGeneratingBanner] = useState(false);

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [eventToDelete, setEventToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteScope, setDeleteScope] = useState('this');

    // Derived filtered events
    const events = filter === 'upcoming'
        ? allEvents.filter(e => isUpcoming(e.dateTime))
        : allEvents.filter(e => !isUpcoming(e.dateTime)).reverse();

    // --- Fetch Events ---
    const fetchEvents = useCallback(async () => {
        if (!userOrgId) {
            if (!loadingAuth && currentUser) {
                setError("Organization details missing from your account. Cannot load events.");
            }
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);

        try {
            const eventsQuery = query(
                collection(db, 'events'),
                where('orgId', '==', userOrgId),
                orderBy('dateTime', 'asc')
            );
            const eventsSnap = await getDocs(eventsQuery);

            const fetchedEvents = eventsSnap.docs.map(d => ({
                ...d.data(),
                id: d.id
            }));

            setAllEvents(fetchedEvents);
            setError(null);
        } catch (err) {
            console.error("EventsPage: Error fetching events", err);
            setError(err.message || "Failed to load events.");
        } finally {
            setLoading(false);
        }
    }, [userOrgId, loadingAuth, currentUser]);

    useEffect(() => {
        if (!loadingAuth && userOrgId) {
            fetchEvents();
        } else if (!loadingAuth && !userOrgId && currentUser) {
            setError("Organization details missing from your account. Cannot load events.");
            setLoading(false);
        } else if (!loadingAuth && !currentUser) {
            setAllEvents([]);
            setLoading(false);
            setError(null);
        }
    }, [loadingAuth, userOrgId, fetchEvents, currentUser]);

    // Auto-refresh after recurring event creation (Cloud Function needs a few seconds)
    useEffect(() => {
        if (location.state?.recurringCreated) {
            setGeneratingBanner(true);
            // Clear the state so refresh doesn't re-trigger
            window.history.replaceState({}, document.title);
            const timer = setTimeout(() => {
                fetchEvents().then(() => setGeneratingBanner(false));
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [location.state, fetchEvents]);

    // --- Delete Handler ---
    const handleDeleteClick = (event) => {
        setEventToDelete(event);
        setDeleteScope('this');
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!eventToDelete || !userOrgId || !isAdmin) return;

        setIsDeleting(true);
        try {
            const deleteEventCallable = httpsCallable(functions, 'deleteEvent');
            const payload = { eventId: eventToDelete.id };

            if (eventToDelete.isRecurring && eventToDelete.seriesId) {
                payload.scope = deleteScope;
            }

            await deleteEventCallable(payload);

            // Refresh events from server after successful delete
            await fetchEvents();

            setShowDeleteModal(false);
            setEventToDelete(null);
        } catch (err) {
            console.error("Error deleting event:", err);
            setError("Failed to delete event. Please try again.");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCloseDeleteModal = () => {
        if (!isDeleting) {
            setShowDeleteModal(false);
            setEventToDelete(null);
        }
    };

    // --- Render Logic ---

    // Loading state
    if (loadingAuth || loading) {
        return (
            <div className="events-page">
                <div className="events-loading">
                    <div className="skeleton-header"></div>
                    <div className="skeleton-event-card"></div>
                    <div className="skeleton-event-card"></div>
                    <div className="skeleton-event-card"></div>
                </div>
            </div>
        );
    }

    // Not logged in
    if (!currentUser) {
        return (
            <div className="events-page">
                <div className="container mt-3">
                    <Alert variant="warning">
                        Please <Alert.Link as={Link} to="/login">log in</Alert.Link> to view events.
                    </Alert>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="events-page">
                <div className="events-header">
                    <div className="container">
                        <div className="events-header-content">
                            <div>
                                <h1 className="events-title">Events</h1>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        Error: {error}
                    </Alert>
                </div>
                {isAdmin && (
                    <Link to="/events/add" className="fab-create-event">
                        <FontAwesomeIcon icon={faPlus} />
                        <span>Create Event</span>
                    </Link>
                )}
            </div>
        );
    }

    return (
        <div className="events-page">
            {/* Page Header */}
            <div className="events-header">
                <div className="container">
                    <div className="events-header-content">
                        <div>
                            <h1 className="events-title">Events</h1>
                            {events.length > 0 && (
                                <div className="events-count">
                                    {events.length} {filter === 'upcoming' ? 'upcoming' : 'past'} event{events.length !== 1 ? 's' : ''}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter Tabs */}
            <div className="container">
                <div className="events-filter-tabs">
                    <button
                        className={`events-filter-tab ${filter === 'upcoming' ? 'active' : ''}`}
                        onClick={() => setFilter('upcoming')}
                    >
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        <span>Upcoming</span>
                    </button>
                    <button
                        className={`events-filter-tab ${filter === 'past' ? 'active' : ''}`}
                        onClick={() => setFilter('past')}
                    >
                        <FontAwesomeIcon icon={faHistory} />
                        <span>Past</span>
                    </button>
                </div>
            </div>

            <div className="container">
                {/* Generating recurring events banner */}
                {generatingBanner && (
                    <div style={{
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        borderRadius: '8px',
                        padding: '0.75rem 1rem',
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        color: '#1e40af'
                    }}>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Generating recurring event occurrences...</span>
                    </div>
                )}

                {/* Empty State */}
                {events.length === 0 && !generatingBanner && (
                    <div className="events-empty">
                        <div className="events-empty-icon">
                            <FontAwesomeIcon icon={filter === 'upcoming' ? faCalendarAlt : faHistory} />
                        </div>
                        <h3>{filter === 'upcoming' ? 'No Upcoming Events' : 'No Past Events'}</h3>
                        <p>
                            {filter === 'past'
                                ? "Past events will appear here after they have occurred."
                                : isAdmin
                                    ? "Create your first event to keep your organization informed about upcoming activities."
                                    : "No events have been scheduled yet. Check back soon!"
                            }
                        </p>
                        {filter === 'upcoming' && isAdmin && (
                            <Link to="/events/add" className="events-empty-cta">
                                <FontAwesomeIcon icon={faPlus} /> Create First Event
                            </Link>
                        )}
                    </div>
                )}

                {/* Event Cards */}
                {events.map(event => (
                    <div
                        key={event.id}
                        className={`event-card${filter === 'past' ? ' event-card-past' : ''}${event.photo ? ' event-card-with-photo' : ''}`}
                        onClick={() => navigate(`/events/${event.id}`)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && navigate(`/events/${event.id}`)}
                    >
                        {event.photo ? (
                            <div className="event-card-banner">
                                <EventCardPhoto photoPath={event.photo} />
                                <div className="event-banner-date">
                                    <span className="event-date-month">
                                        {event.dateTime && (event.dateTime instanceof Timestamp
                                            ? event.dateTime.toDate()
                                            : new Date(event.dateTime)
                                        ).toLocaleDateString('en-US', { month: 'short' })}
                                    </span>
                                    <span className="event-date-day">
                                        {event.dateTime && (event.dateTime instanceof Timestamp
                                            ? event.dateTime.toDate()
                                            : new Date(event.dateTime)
                                        ).getDate()}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="event-card-date">
                                <div className="event-date-month">
                                    {event.dateTime && (event.dateTime instanceof Timestamp
                                        ? event.dateTime.toDate()
                                        : new Date(event.dateTime)
                                    ).toLocaleDateString('en-US', { month: 'short' })}
                                </div>
                                <div className="event-date-day">
                                    {event.dateTime && (event.dateTime instanceof Timestamp
                                        ? event.dateTime.toDate()
                                        : new Date(event.dateTime)
                                    ).getDate()}
                                </div>
                            </div>
                        )}
                        <div className="event-card-content">
                            <h2 className="event-title">
                                {event.title}
                                {event.isRecurring && (
                                    <span className="event-recurring-badge" title="Recurring event">
                                        <FontAwesomeIcon icon={faRepeat} />
                                    </span>
                                )}
                            </h2>
                            <div className="event-datetime">
                                <FontAwesomeIcon icon={faClock} className="event-time-icon" />
                                <span>{formatEventDate(event.dateTime)} at {formatEventTime(event.dateTime)}</span>
                            </div>
                            {event.description && (
                                <p className="event-description">{event.description}</p>
                            )}
                        </div>
                        {isAdmin && (
                            <div className="event-card-actions" onClick={(e) => e.stopPropagation()}>
                                <button
                                    className="event-action-btn event-action-btn-edit"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/events/${event.id}/edit`);
                                    }}
                                    title="Edit Event"
                                >
                                    <FontAwesomeIcon icon={faEdit} />
                                </button>
                                <button
                                    className="event-action-btn event-action-btn-delete"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteClick(event);
                                    }}
                                    title="Delete Event"
                                >
                                    <FontAwesomeIcon icon={faTrashAlt} />
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* FAB Button */}
            {isAdmin && (
                <Link to="/events/add" className="fab-create-event">
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Create Event</span>
                </Link>
            )}

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered className="events-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Delete Event</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete <strong>"{eventToDelete?.title}"</strong>?</p>
                    {eventToDelete?.isRecurring && eventToDelete?.seriesId && (
                        <div style={{ marginBottom: '1rem' }}>
                            <p style={{ fontWeight: 500, marginBottom: '0.5rem' }}>This is a recurring event. What would you like to delete?</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {[
                                    { value: 'this', label: 'This event only', desc: 'Remove just this occurrence' },
                                    { value: 'future', label: 'This and all future events', desc: 'Keep past occurrences' },
                                    { value: 'all', label: 'All events in series', desc: 'Delete the entire series' },
                                ].map(opt => (
                                    <label key={opt.value} style={{
                                        display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                                        padding: '0.5rem 0.75rem', borderRadius: '8px', cursor: 'pointer',
                                        background: deleteScope === opt.value ? '#fef2f2' : '#f8fafc',
                                        border: `1px solid ${deleteScope === opt.value ? '#fecaca' : '#e2e8f0'}`
                                    }}>
                                        <input
                                            type="radio" name="deleteScope"
                                            value={opt.value}
                                            checked={deleteScope === opt.value}
                                            onChange={(e) => setDeleteScope(e.target.value)}
                                            disabled={isDeleting}
                                            style={{ marginTop: '0.25rem' }}
                                        />
                                        <div>
                                            <div style={{ fontWeight: 500 }}>{opt.label}</div>
                                            <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{opt.desc}</div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                    <p className="text-muted mb-0">This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDelete} disabled={isDeleting}>
                        {isDeleting ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            "Delete Event"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default EventsPage;
